<template>
  <div class="apt-map-page">
    <div class="container-fluid">
      <div class="row">


        <!-- Content -->
        <div class="col-12">
          <h1 class="mb-4">{{ content.title }}</h1>
          
                     <!-- APT Group Selection -->
           <SelectAPT :aptGroups="aptGroups" :selectedAPT="selectedAPT" @select-apt="selectAPTGroup" />

                     <!-- Display APT group details above the chart -->
           <div v-if="selectedAPT && selectedAPT.type !== 'all'" class="card mb-3">
             <div class="card-body">
               <h4 class="card-title">{{ selectedAPT.name }}</h4>
               <p class="card-text"><strong>{{ content.summary }}:</strong> {{ selectedAPT.aptSummary }}</p>
               <p class="card-text"><strong>{{ content.updated }}:</strong> {{ selectedAPT.updatedDate }} | <strong>{{ content.version }}:</strong> {{ selectedAPT.version }}</p>
               <p class="card-text"><strong>{{ content.targetTags }}:</strong> {{ selectedAPT.aptTargetProfileTags.join(', ') }}</p>
               
               <!-- Latent Variables (Visibility & Destructiveness) -->
               <div v-if="selectedAPT.latentVariables" class="latent-variables-section mt-3 pt-3 border-top">
                 <h5 class="mb-3">{{ content.latentVariables.title }}</h5>
                 <div class="row">
                   <div class="col-md-6 mb-2">
                     <strong>{{ content.latentVariables.visibility }}:</strong>
                     <span class="badge bg-warning text-dark ms-2">{{ selectedAPT.latentVariables.Visibility }}</span>
                   </div>
                   <div class="col-md-6 mb-2">
                     <strong>{{ content.latentVariables.destructiveness }}:</strong>
                     <span class="badge bg-danger ms-2">{{ selectedAPT.latentVariables.Destructiveness }}</span>
                   </div>
                 </div>
               </div>
             </div>
           </div>

           

                                          <!-- RadarChart and Recommendation Panel -->
           <div class="row mt-4">
             <div :class="selectedAPT.type === 'all' ? 'col-12' : 'col-md-8'">
               <div v-if="selectedAPT.type === 'all' && aptGroups.length === 0" class="loading-container">
                 <div class="loading-spinner"></div>
                 <p>{{ content.loading }}</p>
               </div>
               <RadarChart 
                 v-else-if="selectedAPT && (selectedAPT.type === 'all' ? aptGroups.length > 0 : selectedAPT.bigFive)" 
                 :aptData="selectedAPT" 
                 :allAptGroups="selectedAPT.type === 'all' ? aptGroups : null"
                 :baseAdvice="baseAdvice"
                 :language="language"
                 @score-click="handleScoreClick"
               />
             </div>
             <div class="col-md-4" v-if="selectedAPT.type !== 'all'">
                <div v-if="selectedRecommendation" class="recommendation-panel">
                  <div class="recommendation-header">
                    <strong>{{ content.recommendation }}</strong>
                  </div>
                  <div class="recommendation-content">
                    <div v-if="selectedRecommendation.aptName" class="recommendation-item">
                      <strong>{{ content.aptName }}</strong>
                      <div>{{ selectedRecommendation.aptName }}</div>
                    </div>
                    <div class="recommendation-item">
                      <strong>{{ content.parameter }}</strong>
                      <div>{{ selectedRecommendation.trait }}</div>
                    </div>
                    <div class="recommendation-item">
                      <strong>{{ content.score }}</strong>
                      <div>{{ selectedRecommendation.score }}/5</div>
                    </div>
                    <div v-if="selectedRecommendation.indicator" class="recommendation-item">
                      <strong>{{ content.indicator }}</strong>
                      <div>{{ selectedRecommendation.indicator }}</div>
                    </div>
                    <div v-if="selectedRecommendation.defense" class="recommendation-item">
                      <strong>{{ content.defense }}</strong>
                      <div>{{ selectedRecommendation.defense }}</div>
                    </div>
                  </div>
                </div>
                <div v-else class="recommendation-placeholder">
                  <div class="placeholder-content">
                    <i class="placeholder-icon">📊</i>
                    <p>{{ content.clickInstruction }}</p>
                  </div>
                </div>
              </div>
           </div>
                 </div>
       </div>
     </div>
 
     <!-- Cluster Profile Information -->
     <div v-if="selectedCluster" class="container-fluid mt-5">
       <div class="row">
         <div class="col-12">
           <div class="cluster-section">
             <h2 class="cluster-title">{{ content.cluster.title }}</h2>
             <p class="cluster-description">{{ content.cluster.description }}</p>
             
             <div class="cluster-details">
               <!-- Cluster Criteria -->
               <div v-if="selectedCluster.cluster_criteria" class="cluster-criteria mb-4">
                 <h5>{{ content.cluster.criteria }}</h5>
                 <div class="criteria-badges">
                   <span class="badge bg-warning me-2">
                     <strong>{{ content.cluster.visibility }}:</strong> {{ selectedCluster.cluster_criteria.Visibility }}
                   </span>
                   <span class="badge bg-danger me-2">
                     <strong>{{ content.cluster.destructiveness }}:</strong> {{ selectedCluster.cluster_criteria.Destructiveness }}
                   </span>
                 </div>
                 <p v-if="selectedCluster.cluster_criteria.description" class="mt-2">
                   {{ selectedCluster.cluster_criteria.description }}
                 </p>
               </div>
               
               <div class="cluster-macro-profile">
                 <h5>{{ content.cluster.macroProfile }}</h5>
                 <p>{{ selectedCluster.macro_profile }}</p>
               </div>
               
               <div class="cluster-operational-profile">
                 <h5>{{ content.cluster.operationalProfile }}</h5>
                 <p>{{ selectedCluster.operational_profile }}</p>
               </div>
               
               <div class="cluster-defense-strategies">
                 <h5>{{ content.cluster.defenseStrategies }}</h5>
                 
                 <!-- Key Traits and Defensive Focus -->
                 <div v-if="selectedCluster.Defense.key_traits || selectedCluster.Defense.defensive_focus" class="defense-overview mb-3 p-3 bg-light rounded">
                   <div v-if="selectedCluster.Defense.key_traits" class="mb-2">
                     <strong>{{ content.cluster.keyTraits }}:</strong>
                     <span class="ms-2">{{ selectedCluster.Defense.key_traits }}</span>
                   </div>
                   <div v-if="selectedCluster.Defense.defensive_focus">
                     <strong>{{ content.cluster.defensiveFocus }}:</strong>
                     <span class="ms-2">{{ selectedCluster.Defense.defensive_focus }}</span>
                   </div>
                 </div>
                 
                 <div class="defense-category">
                   <h6>{{ content.cluster.mainMitigation }}</h6>
                   <ul>
                     <li v-for="(mitigation, idx) in selectedCluster.Defense.main_mitigation" :key="idx">
                       {{ mitigation }}
                     </li>
                   </ul>
                 </div>
                 
                 <div class="defense-category">
                   <h6>{{ content.cluster.preventiveDefense }}</h6>
                   <ul>
                     <li v-for="(defense, idx) in selectedCluster.Defense.preventive_defense" :key="idx">
                       {{ defense }}
                     </li>
                   </ul>
                 </div>
                 
                 <div class="defense-category">
                   <h6>{{ content.cluster.disruptiveMeasure }}</h6>
                   <p>{{ selectedCluster.Defense.disruptive_measure }}</p>
                 </div>
               </div>
             </div>
           </div>
         </div>
       </div>
     </div>
 
     <!-- Sources Section -->
    <div v-if="selectedAPT.type !== 'all'" class="container-fluid mt-5">
      <div class="row">
        <div class="col-12">
          <div class="sources-section">
            <h2 class="sources-title">{{ content.sources.title }}</h2>
            <p class="sources-description">{{ content.sources.description }}</p>
            
            <div v-if="sourcesData && sourcesData.sources && baseAPTName" class="sources-grid">
              <div v-if="aptSources" class="source-item">
                <h4 class="apt-group-name">{{ selectedAPT.name }}</h4>
                <div class="urls-list">
                  <a 
                    v-for="(url, index) in aptSources" 
                    :key="index"
                    :href="url" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    class="source-link-plain"
                  >
                    {{ url }}
                  </a>
                </div>
              </div>
              <div v-else class="no-sources-message">
                <p>{{ content.sources.noSources }}</p>
              </div>
            </div>
            
            <div v-if="sourcesData && sourcesData.metadata" class="sources-metadata">
              <p class="last-updated">
                <strong>{{ content.sources.lastUpdated }}:</strong> {{ sourcesData.metadata.last_updated }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SelectAPT from '../components/SelectAPT.vue'
import RadarChart from '../components/RadarChart.vue'
import DataService from '../services/DataServices'
import { languageStore } from '../stores/languageStore'

export default {
  name: 'APTMap',
  components: { SelectAPT, RadarChart },
  data() {
    return {
      aptGroups: [],          // All APT groups loaded from GitHub
      selectedAPT: { id: 'all', name: 'All APTs', type: 'all' },      // The currently selected APT group
      baseAdvice: {},         // Base JSON mapping scores to recommendations
      selectedRecommendation: '', // Recommendation to display when a score is clicked
      sourcesData: {},        // Sources data loaded from JSON
      clusterProfiles: [],    // Cluster profiles data
      selectedCluster: null,  // Cluster information for selected APT
      content: {
        en: {
          title: 'APT Group Analysis',
          summary: 'Summary',
          updated: 'Updated',
          version: 'Version',
          targetTags: 'Target Tags',
          recommendation: 'Recommendation',
          aptName: 'APT Group',
          parameter: 'Parameter',
          score: 'Score',
          indicator: 'Indicator',
          defense: 'Defense Advice',
          noRecommendation: 'No recommendation available.',
          clickInstruction: 'Click on any point in the radar chart to see detailed analysis and defense recommendations.',
          allViewInstruction: 'All APTs are displayed on the radar chart. Select an individual APT to see detailed recommendations.',
          loading: 'Loading APT data...',
          sources: {
            title: 'Sources',
            description: 'List of sources used for APT group analysis and behavioral profiling.',
            lastUpdated: 'Last Updated',
            viewSource: 'View Source',
            noSources: 'No specific sources available for this APT group.'
          },
          latentVariables: {
            title: 'Latent Variables',
            visibility: 'Visibility (V)',
            destructiveness: 'Destructiveness (D)',
            formulas: 'Formulas'
          },
          cluster: {
            title: 'Cluster Profile',
            description: 'This APT group belongs to the following behavioral cluster:',
            noCluster: 'No cluster profile found for this APT group.',
            criteria: 'Cluster Criteria',
            visibility: 'Visibility',
            destructiveness: 'Destructiveness',
            macroProfile: 'Macro Profile',
            centralTraits: 'Central Traits',
            operationalProfile: 'Operational Profile',
            defenseStrategies: 'Defense Strategies',
            keyTraits: 'Key Traits',
            defensiveFocus: 'Defensive Focus',
            mainMitigation: 'Main Mitigation',
            preventiveDefense: 'Preventive Defense',
            disruptiveMeasure: 'Disruptive Measure'
          }
        },
        es: {
          title: 'Análisis de Grupos APT',
          summary: 'Resumen',
          updated: 'Actualizado',
          version: 'Versión',
          targetTags: 'Etiquetas de Objetivo',
          recommendation: 'Recomendación',
          aptName: 'Grupo APT',
          parameter: 'Parámetro',
          score: 'Puntuación',
          indicator: 'Indicador',
          defense: 'Consejo de Defensa',
          noRecommendation: 'No hay recomendación disponible.',
          clickInstruction: 'Haz clic en cualquier punto del gráfico de radar para ver el análisis detallado y las recomendaciones de defensa.',
          allViewInstruction: 'Todos los grupos APT se muestran en el gráfico de radar. Selecciona un APT individual para ver recomendaciones detalladas.',
          loading: 'Cargando datos APT...',
          sources: {
            title: 'Fuentes',
            description: 'Lista de fuentes utilizadas para el análisis de grupos APT y el perfilado conductual.',
            lastUpdated: 'Última Actualización',
            viewSource: 'Ver Fuente',
            noSources: 'No hay fuentes específicas disponibles para este grupo APT.'
          },
          latentVariables: {
            title: 'Variables Latentes',
            visibility: 'Visibilidad (V)',
            destructiveness: 'Destructividad (D)',
            formulas: 'Fórmulas'
          },
          cluster: {
            title: 'Perfil de Cluster',
            description: 'Este grupo APT pertenece al siguiente cluster conductual:',
            noCluster: 'No se encontró perfil de cluster para este grupo APT.',
            criteria: 'Criterios del Cluster',
            visibility: 'Visibilidad',
            destructiveness: 'Destructividad',
            macroProfile: 'Perfil Macro',
            centralTraits: 'Rasgos Centrales',
            operationalProfile: 'Perfil Operacional',
            defenseStrategies: 'Estrategias de Defensa',
            keyTraits: 'Rasgos Clave',
            defensiveFocus: 'Enfoque Defensivo',
            mainMitigation: 'Mitigación Principal',
            preventiveDefense: 'Defensa Preventiva',
            disruptiveMeasure: 'Medida Disruptiva'
          }
        }
      }
    }
  },
  computed: {
    language() {
      return languageStore.getLanguage()
    },
    content() {
      return this.content[this.language]
    },
    baseAPTName() {
      if (!this.selectedAPT || !this.selectedAPT.name) return '';
      // Extract the base name before parentheses (e.g., "APT28" from "APT28 (Fancy Bear / Sofacy / Pawn Storm)")
      return this.selectedAPT.name.split(' (')[0];
    },
    aptSources() {
      if (!this.sourcesData || !this.sourcesData.sources || !this.baseAPTName) return null;
      
      // First try the exact base name
      if (this.sourcesData.sources[this.baseAPTName]) {
        return this.sourcesData.sources[this.baseAPTName];
      }
      
      // If not found, try to find by aliases
      const aliases = this.getAliases();
      for (const alias of aliases) {
        if (this.sourcesData.sources[alias]) {
          return this.sourcesData.sources[alias];
        }
      }
      
      return null;
    },
    selectedCluster() {
      if (!this.selectedAPT || this.selectedAPT.type === 'all' || !this.clusterProfiles.length) {
        return null;
      }
      
      // Find cluster that contains this APT
      return this.clusterProfiles.find(cluster => 
        cluster.APT && cluster.APT.includes(this.baseAPTName)
      );
    }
  },
  watch: {
    language() {
      // Reload data when language changes
      this.loadData();
    },
    '$route.query': {
      handler() {
        this.checkForAPTQuery();
      },
      immediate: true
    }
  },
  methods: {
    loadData() {
      // Get language-specific URLs based on current language
      const currentLang = this.language;
      const aptGroupsUrl = `https://raw.githubusercontent.com/Base4Security/PRISM/refs/heads/main/aptGroups/aptGroups${currentLang.toUpperCase()}.json`;
      const baseAdviceUrl = `https://raw.githubusercontent.com/Base4Security/PRISM/refs/heads/main/base/baseAdvice${currentLang.toUpperCase()}.json`;
      const sourcesUrl = `https://raw.githubusercontent.com/Base4Security/PRISM/refs/heads/main/aptGroups/aptGroupsSources.json`;
      const clusterProfileUrl = `https://raw.githubusercontent.com/Base4Security/PRISM/refs/heads/main/aptGroups/ClusterProfile${currentLang.toUpperCase()}.json`;

      DataService.fetchJSON(aptGroupsUrl).then(data => {
        // Check if the fetched data has an 'apts' property. If yes, use that array.
        this.aptGroups = data.apts ? data.apts : (Array.isArray(data) ? data : [data]);
        
        // Set "All" as default selection
        this.selectedAPT = { id: 'all', name: 'All APTs', type: 'all' };
        
        // Check for APT query parameter after data is loaded
        this.checkForAPTQuery();
      });

      DataService.fetchJSON(baseAdviceUrl).then(data => {
        this.baseAdvice = data;
      });

      DataService.fetchJSON(sourcesUrl).then(data => {
        this.sourcesData = data;
      });

      DataService.fetchJSON(clusterProfileUrl).then(data => {
        if (data && data.ClusterProfile) {
          this.clusterProfiles = data.ClusterProfile;
        } else {
          this.clusterProfiles = [];
        }
      });
    },
    selectAPTGroup(apt) {
      this.selectedAPT = apt;
      this.selectedRecommendation = '';
    },
    handleScoreClick({ trait, score, indicator, defense, aptName }) {
      // Store the detailed recommendation information
      this.selectedRecommendation = {
        trait,
        score,
        indicator,
        defense,
        aptName
      };
    },
    getAliases() {
      if (!this.selectedAPT || !this.selectedAPT.name) return [];
      
      // Extract aliases from parentheses
      const match = this.selectedAPT.name.match(/\((.*?)\)/);
      if (!match) return [];
      
      // Split by '/' and trim whitespace
      return match[1].split('/').map(alias => alias.trim());
    },
    checkForAPTQuery() {
      const aptQuery = this.$route.query.apt;
      if (aptQuery && this.aptGroups.length > 0) {
        // Find the APT group by short name (e.g., APT29)
        const aptGroup = this.aptGroups.find(apt => {
          if (!apt.name) return false;
          
          // Extract the base name before parentheses (e.g., "APT29" from "APT29 (Cozy Bear)")
          const baseName = apt.name.split(' (')[0];
          
          // Check if the query matches the base name exactly
          return baseName === aptQuery;
        });
        
        if (aptGroup) {
          this.selectAPTGroup(aptGroup);
          // Clear the query parameter
          this.$router.replace({ query: {} });
        }
      }
    }
  },
  mounted() {
    this.loadData();
    // Periodic refresh every 5 minutes (300,000 ms)
    setInterval(this.loadData, 300000);
  }
}
</script>

<style scoped>
.apt-map-page {
  padding: 20px;
  background: var(--mm-custom-color-1);
}

.card {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  border: none;
  background: var(--mm-custom-color-3);
  color: var(--mm-custom-color-5);
}

.card h4 {
  color: var(--mm-custom-color-2);
}

.card p {
  color: var(--mm-custom-color-5);
}

/* Latent Variables Section */
.latent-variables-section {
  border-top: 2px solid var(--mm-custom-color-2);
}

.latent-variables-section h5 {
  color: var(--mm-custom-color-2);
  font-size: 1.1rem;
  margin-bottom: 15px;
}

.latent-variables-section .badge {
  font-size: 0.9rem;
  padding: 5px 10px;
}

.formula-info {
  font-size: 0.85rem;
  line-height: 1.6;
}

.formula-info strong {
  color: var(--mm-custom-color-2);
}

.btn-group .btn {
  min-width: 60px;
}

/* Dark theme enhancements */
h1 {
  color: var(--mm-custom-color-2);
}

/* Cluster Info Card Styles */
.cluster-info-card {
  border-left: 4px solid var(--mm-custom-color-2);
}

.cluster-details {
  margin-top: 20px;
}

.cluster-criteria {
  margin-bottom: 25px;
  padding: 15px;
  background: var(--mm-custom-color-1);
  border-radius: 8px;
  border-left: 4px solid var(--mm-custom-color-2);
}

.cluster-criteria h5 {
  color: var(--mm-custom-color-2);
  font-size: 1.1rem;
  margin-bottom: 10px;
}

.criteria-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 10px;
  background: var(--mm-custom-color-1);
}

.criteria-badges .badge {
  font-size: 0.9rem;
  padding: 6px 12px;
}

.cluster-macro-profile,
.cluster-operational-profile,
.cluster-defense-strategies {
  margin-bottom: 25px;
}

.defense-overview {
  background: var(--mm-custom-color-1);
  padding: 15px;
  border-radius: 8px;
  border-left: 4px solid var(--mm-custom-color-2);
  margin-bottom: 20px;
}

.defense-overview div {
  margin-bottom: 10px;
  color: var(--mm-custom-color-5);
  font-size: 0.95rem;
  line-height: 1.5;
}

.defense-overview div:last-child {
  margin-bottom: 0;
}

.defense-overview strong {
  color: var(--mm-custom-color-2);
  margin-right: 8px;
}

.cluster-macro-profile h5,
.cluster-operational-profile h5,
.cluster-defense-strategies h5 {
  color: var(--mm-custom-color-2);
  font-size: 1.1rem;
  margin-bottom: 10px;
  border-bottom: 1px solid rgba(220, 120, 15, 0.3);
  padding-bottom: 5px;
}

.cluster-macro-profile p,
.cluster-operational-profile p {
  color: var(--mm-custom-color-5);
  line-height: 1.6;
  margin: 0;
}

.defense-category {
  margin-bottom: 20px;
  padding: 15px;
  background: var(--mm-custom-color-1);
  border-radius: 8px;
  border-left: 3px solid var(--mm-custom-color-2);
}

.defense-category h6 {
  color: var(--mm-custom-color-2);
  font-size: 1rem;
  margin-bottom: 10px;
  font-weight: bold;
}

.defense-category ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.defense-category li {
  color: var(--mm-custom-color-5);
  padding: 5px 0;
  border-bottom: 1px solid rgba(220, 120, 15, 0.1);
  line-height: 1.5;
}

.defense-category li:last-child {
  border-bottom: none;
}

.defense-category li::before {
  content: "•";
  color: var(--mm-custom-color-2);
  font-weight: bold;
  margin-right: 8px;
}

.defense-category p {
  color: var(--mm-custom-color-5);
  line-height: 1.5;
  margin: 0;
  font-style: italic;
}

/* Recommendation Panel Styles */
.recommendation-panel {
  background: var(--mm-custom-color-1);
  border: 2px solid var(--mm-custom-color-2);
  border-radius: 15px;
  padding: 20px;
  height: 100%;
  min-height: 400px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
  position: sticky;
  top: 20px;
}

.recommendation-placeholder {
  background: var(--mm-custom-color-1);
  border: 2px dashed var(--mm-custom-color-2);
  border-radius: 15px;
  padding: 40px 20px;
  height: 100%;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
  position: sticky;
  top: 20px;
}

.placeholder-content {
  color: var(--mm-custom-color-5);
}

.placeholder-icon {
  font-size: 3rem;
  margin-bottom: 15px;
  display: block;
  opacity: 0.7;
}

.placeholder-content p {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
  opacity: 0.8;
}

.recommendation-header {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--mm-custom-color-2);
  font-size: 1.2rem;
  color: var(--mm-custom-color-2);
  text-align: center;
}

.recommendation-content {
  line-height: 1.7;
}

.recommendation-item {
  margin-bottom: 20px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(220, 120, 15, 0.2);
}

.recommendation-item:last-child {
  margin-bottom: 0;
  border-bottom: none;
}

.recommendation-item strong {
  color: var(--mm-custom-color-2);
  display: block;
  margin-bottom: 8px;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.recommendation-item:not(:has(strong)) {
  color: var(--mm-custom-color-5);
  font-size: 0.95rem;
}

/* Loading styles */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: var(--mm-custom-color-3);
  border-radius: 15px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--mm-custom-color-2);
  min-height: 400px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(220, 120, 15, 0.3);
  border-top: 4px solid #DC780F;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-container p {
  color: var(--mm-custom-color-5);
  margin: 0;
  font-size: 1.1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .recommendation-panel,
  .recommendation-placeholder {
    margin-top: 20px;
    position: static;
  }
}

  /* Cluster Section Styles */
 .cluster-section {
   background: var(--mm-custom-color-3);
   border-radius: 15px;
   padding: 30px;
   box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
   border: 2px solid var(--mm-custom-color-2);
   margin-top: 40px;
 }
 
 .cluster-title {
   color: var(--mm-custom-color-2);
   margin-bottom: 15px;
   font-size: 1.8rem;
   text-align: center;
 }
 
 .cluster-description {
   color: var(--mm-custom-color-5);
   text-align: center;
   margin-bottom: 30px;
   font-size: 1.1rem;
   opacity: 0.9;
 }
 
 /* Sources Section Styles */
  .sources-section {
    background: var(--mm-custom-color-3);
    border-radius: 15px;
    padding: 30px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
    border: 2px solid var(--mm-custom-color-2);
    margin-top: 40px;
  }

 .sources-title {
   color: var(--mm-custom-color-2);
   margin-bottom: 15px;
   font-size: 1.8rem;
   text-align: center;
 }

 .sources-description {
   color: var(--mm-custom-color-5);
   text-align: center;
   margin-bottom: 30px;
   font-size: 1.1rem;
   opacity: 0.9;
 }

 .sources-grid {
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
   gap: 25px;
   margin-bottom: 30px;
 }

 .source-item {
   background: var(--mm-custom-color-1);
   border: 1px solid var(--mm-custom-color-2);
   border-radius: 10px;
   padding: 20px;
   transition: all 0.3s ease;
 }

 .source-item:hover {
   transform: translateY(-2px);
   box-shadow: 0 6px 20px rgba(220, 120, 15, 0.2);
 }

 .apt-group-name {
   color: var(--mm-custom-color-2);
   margin-bottom: 15px;
   font-size: 1.2rem;
   border-bottom: 2px solid var(--mm-custom-color-2);
   padding-bottom: 8px;
 }

 .urls-list {
   display: flex;
   flex-direction: column;
   gap: 10px;
 }

 .source-link {
   color: #DC780F;
   text-decoration: none;
   padding: 8px 12px;
   border: 1px solid #DC780F;
   border-radius: 6px;
   transition: all 0.3s ease;
   font-size: 0.9rem;
   text-align: center;
 }

 .source-link:hover {
   background-color: #DC780F;
   color: white;
   text-decoration: none;
 }

 .source-link-plain {
   color: #DC780F;
   text-decoration: underline;
   padding: 4px 0;
   font-size: 0.9rem;
   word-break: break-all;
   line-height: 1.4;
 }

 .source-link-plain:hover {
   color: #b3660c;
   text-decoration: none;
 }

 .sources-metadata {
   text-align: center;
   padding-top: 20px;
   border-top: 1px solid var(--mm-custom-color-2);
 }

 .last-updated {
   color: var(--mm-custom-color-5);
   margin: 0;
   font-size: 0.95rem;
   opacity: 0.8;
 }

 .no-sources-message {
   text-align: center;
   color: var(--mm-custom-color-5);
   font-style: italic;
   padding: 20px;
 }

   /* Responsive adjustments for sources */
  @media (max-width: 768px) {
    .sources-grid {
      grid-template-columns: 1fr;
      gap: 20px;
    }
    
    .sources-section,
    .cluster-section {
      padding: 20px;
      margin-top: 30px;
    }
    
    .sources-title,
    .cluster-title {
      font-size: 1.5rem;
    }
  }
</style>
